import React from 'react';
import CompanyLegal from '../components/CompanyLegal';

const SECTIONS = [
  {
    id: 'frameworks', num: '1', title: 'Security framework alignment',
    body: (
      <>
        <ul className="co-chips">
          <li>SOC 2 Type II controls</li>
          <li>NIST Cybersecurity Framework (CSF)</li>
          <li>CIS Controls v8</li>
          <li>ISO 27001 principles</li>
        </ul>
        <p>
          Our cloud hosting infrastructure (Microsoft Azure) holds SOC 2 Type II, ISO 27001,
          ISO 27017, ISO 27018, CSA STAR, and FedRAMP certifications.
        </p>
      </>
    ),
  },
  {
    id: 'access', num: '2', title: 'Access control',
    body: (
      <ul>
        <li>Role-based access control (RBAC) across systems and client environments</li>
        <li>Least privilege enforced for all accounts</li>
        <li>MFA required for production systems and client data access</li>
        <li>Unique individual credentials; shared accounts are prohibited</li>
        <li>Quarterly access reviews; account deactivation within 24 hours of departure</li>
      </ul>
    ),
  },
  {
    id: 'data', num: '3', title: 'Data protection',
    body: (
      <div className="co-subgrid">
        <div className="co-subcard">
          <h3><i className="fa-solid fa-right-left" aria-hidden="true" /> Data in transit</h3>
          <ul>
            <li>TLS 1.2+ for network transmission</li>
            <li>HTTPS enforced on web applications and APIs</li>
            <li>SFTP or encrypted channels for file transfers</li>
          </ul>
        </div>
        <div className="co-subcard">
          <h3><i className="fa-solid fa-database" aria-hidden="true" /> Data at rest</h3>
          <ul>
            <li>AES-256 via Azure Storage Service Encryption (SSE)</li>
            <li>Database encryption on production databases</li>
            <li>Encrypted backups with production-equivalent standards</li>
          </ul>
        </div>
        <div className="co-subcard co-subcard--wide">
          <h3><i className="fa-solid fa-location-dot" aria-hidden="true" /> Data residency</h3>
          <ul>
            <li>
              Client data stored in Canadian data centres (Azure Canada Central, Toronto; Canada East,
              Quebec City) unless otherwise agreed in writing.
            </li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 'network', num: '4', title: 'Network security',
    body: (
      <ul>
        <li>Azure Web Application Firewall (WAF) on client-facing applications</li>
        <li>Azure DDoS Protection Standard enabled</li>
        <li>Network segmentation between client environments</li>
        <li>IP allowlisting for administrative access where applicable</li>
        <li>Rate limiting and bot protection on public endpoints</li>
      </ul>
    ),
  },
  {
    id: 'vulnerability', num: '5', title: 'Vulnerability and patch management',
    body: (
      <ul>
        <li>Critical patches applied within 48 hours of stable vendor release</li>
        <li>Non-critical updates tested in staging and deployed monthly</li>
        <li>Annual penetration testing by qualified professionals</li>
        <li>Regular automated vulnerability scanning of production systems</li>
        <li>Server hardening aligned with CIS Benchmarks</li>
      </ul>
    ),
  },
  {
    id: 'incident', num: '6', title: 'Incident response',
    body: (
      <>
        <p>Our incident response process follows NIST guidance:</p>
        <ol className="co-flow">
          <li>Preparation</li>
          <li>Detection and analysis</li>
          <li>Containment</li>
          <li>Eradication and recovery</li>
          <li>Post-incident review</li>
        </ol>
        <p>For incidents involving client data:</p>
        <ul>
          <li>Client notification within 24 hours of confirmed breach</li>
          <li>Written incident report within 72 hours</li>
          <li>Compliance with Ontario breach notification requirements under FIPPA</li>
        </ul>
      </>
    ),
  },
  {
    id: 'continuity', num: '7', title: 'Business continuity and backup',
    body: (
      <ul>
        <li>Automated daily backups with 30-day retention</li>
        <li>Geo-redundant backup storage across two Canadian Azure regions</li>
        <li>RPO: 24 hours</li>
        <li>RTO: 4 hours for critical systems</li>
        <li>Quarterly backup restoration tests</li>
      </ul>
    ),
  },
  {
    id: 'employees', num: '8', title: 'Employee security',
    body: (
      <ul>
        <li>Annual security awareness training for all team members</li>
        <li>Training includes phishing, secure coding, data handling, incident reporting</li>
        <li>Background checks for personnel with client-data access</li>
        <li>Confidentiality agreements for all team members</li>
      </ul>
    ),
  },
  {
    id: 'third-party', num: '9', title: 'Third-party risk management',
    body: (
      <ul>
        <li>Security evaluation of third-party services before adoption</li>
        <li>Data processing agreements with applicable sub-processors</li>
        <li>Least-scope third-party access</li>
        <li>Regular review of third-party certifications</li>
      </ul>
    ),
  },
  {
    id: 'review', num: '10', title: 'Policy review',
    body: (
      <p>
        This policy is reviewed and updated annually, or sooner based on incidents,
        regulatory changes, or material infrastructure changes.
      </p>
    ),
  },
  {
    id: 'contact', title: 'Contact',
    body: (
      <div className="co-prose__contact">
        <p>
          Security inquiries or vulnerability reports: <br />
          Email: <a href="mailto:security@moneliautomation.com">security@moneliautomation.com</a><br />
          Phone: +1 (905) 746-4835
        </p>
        <address>
          Moneli Automation Inc.<br />
          151 Charles St W, Suite 100<br />
          Kitchener, Ontario N2G 1H6, Canada
        </address>
      </div>
    ),
  },
];

const INTRO = (
  <p>
    Moneli Automation Inc. ("Moneli Automation") is committed to protecting the
    confidentiality, integrity, and availability of all information entrusted to us by clients,
    partners, and employees.
  </p>
);

function SecurityPolicy() {
  return (
    <CompanyLegal
      slug="security"
      crumb="Security Policy"
      title={<>Information security <em>policy</em></>}
      lead="The controls Moneli Automation uses to protect the confidentiality, integrity, and availability of client information."
      meta={[
        { label: 'Last updated', value: 'February 2026' },
        { label: 'Review cycle', value: 'Reviewed annually' },
      ]}
      intro={INTRO}
      sections={SECTIONS}
    />
  );
}

export default SecurityPolicy;
